import { create } from 'zustand';
import { User as FirebaseUser } from 'firebase/auth';
import { onAuthStateChange, getCurrentUserRole } from '@/services/firebase';
import type { UserProfile, UserRole } from '@/types';

interface AuthState {
  user: FirebaseUser | null;
  userProfile: UserProfile | null;
  role: UserRole | null;
  loading: boolean;
  initialized: boolean;
  setUser: (user: FirebaseUser | null) => Promise<void>;
  initialize: () => void;
  signOut: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => {
  return {
    user: null,
    userProfile: null,
    role: null,
    loading: true,
    initialized: false,

    initialize: () => {
      if (get().initialized) return;

      onAuthStateChange(async (user) => {
        if (user) {
          const role = await getCurrentUserRole(user);
          // Fetch full profile if needed
          const { getUserProfile } = await import('@/services/firebase');
          const profile = await getUserProfile(user.uid);

          set({
            user,
            userProfile: profile,
            role,
            loading: false,
          });
        } else {
          set({
            user: null,
            userProfile: null,
            role: null,
            loading: false,
          });
        }
      });

      set({ initialized: true });
    },

    setUser: async (user: FirebaseUser | null) => {
      if (user) {
        const role = await getCurrentUserRole(user);
        const { getUserProfile } = await import('@/services/firebase');
        const profile = await getUserProfile(user.uid);

        set({
          user,
          userProfile: profile,
          role,
          loading: false,
        });
      } else {
        set({
          user: null,
          userProfile: null,
          role: null,
          loading: false,
        });
      }
    },

    signOut: async () => {
      const { signOut } = await import('@/services/firebase');
      await signOut();
      set({
        user: null,
        userProfile: null,
        role: null,
      });
    },
  };
});

